import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Upload,
  Download,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Link2,
  Trash2,
  X,
  Search,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  FileSpreadsheet,
} from "lucide-react";
import { Button } from "@finora/ui";
import { ApiError } from "@finora/api-client";
import type { PfrReceiptsConvertResultDto, PfrReceiptsImportSummaryDto } from "@finora/types";
import { api } from "../../lib/api";
import { cyrillicToLatin } from "../../lib/cyrillicToLatin";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

function downloadXlsxBase64(base64: string, fileName: string) {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
  const blob = new Blob([new Uint8Array(byteNumbers)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

type SortDir = "asc" | "desc";

/**
 * Sortiranje klikom na kolonu (osim "Račun" - nepotrebno, uvek isti broj
 * računa za više artikala) - vidi razgovor "sort po nazivu i statusu mnogo
 * je onda preglednije". `getValue` vraća string ili broj za dati ključ;
 * poređenje bira tip automatski.
 */
function useSort<T>(getValue: (item: T, key: string) => string | number) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const applySort = (items: T[]): T[] => {
    if (!sortKey) return items;
    const copy = [...items];
    copy.sort((a, b) => {
      const va = getValue(a, sortKey);
      const vb = getValue(b, sortKey);
      const cmp =
        typeof va === "number" && typeof vb === "number"
          ? va - vb
          : String(va).localeCompare(String(vb), "sr");
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  };

  return { sortKey, sortDir, toggleSort, applySort };
}

function SortableTh({
  label,
  sortKeyName,
  activeKey,
  dir,
  onClick,
  className,
}: {
  label: string;
  sortKeyName: string;
  activeKey: string | null;
  dir: SortDir;
  onClick: (key: string) => void;
  className?: string;
}) {
  const active = activeKey === sortKeyName;
  return (
    <th className={`pb-1 pr-3 ${className ?? ""}`}>
      <button
        type="button"
        onClick={() => onClick(sortKeyName)}
        className={`flex items-center gap-0.5 font-medium ${active ? "text-brand" : ""}`}
      >
        {label}
        {active ? (
          dir === "asc" ? (
            <ArrowUp className="h-3 w-3" aria-hidden />
          ) : (
            <ArrowDown className="h-3 w-3" aria-hidden />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" aria-hidden />
        )}
      </button>
    </th>
  );
}

/**
 * "Mapiraj" - korisnik jednom poveže naziv artikla iz JSON-a Poreske uprave
 * sa pravim Finora artiklom (live pretraga), sistem to zapamti i sledeći put
 * automatski spoji isto ime - vidi razgovor "kao za izvod kad trazi uplate".
 */
function MapItemModal({
  itemName,
  onClose,
  onMapped,
}: {
  itemName: string;
  onClose: () => void;
  onMapped: (productId: string, sku: string) => void;
}) {
  const [search, setSearch] = useState("");
  // Mala debounce pauza (250ms) - bez nje svaki pritisnut taster odmah gađa
  // API, što kod brzog kucanja zna da "trepće" (stariji odgovor stigne posle
  // novijeg) - vidi razgovor "search box isto live".
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(value), 250);
  };

  // Predlog PRE nego što korisnik počne da traži - da li je ovo ime već
  // ranije ručno mapirano (da se vidi/ispravi greška) i do 5 najsličnijih
  // artikala - vidi razgovor "da predlog npr 5 artikla koji bi najvise
  // odgovarali... automatski u mapiranju upise to u search".
  const { data: suggestion } = useQuery({
    queryKey: ["pfr-mapping-suggestions", itemName],
    queryFn: () => api.getPfrReceiptMappingSuggestions(itemName),
  });
  const initializedSearchRef = useRef(false);
  useEffect(() => {
    if (suggestion && !initializedSearchRef.current) {
      initializedSearchRef.current = true;
      setSearch(suggestion.suggestedSearchTerm);
      setDebouncedSearch(suggestion.suggestedSearchTerm);
    }
  }, [suggestion]);

  const { data: products, isLoading } = useQuery({
    queryKey: ["products-search", debouncedSearch],
    queryFn: () => api.listProducts({ search: debouncedSearch || undefined }),
  });

  const map = useMutation({
    mutationFn: (productId: string) => api.mapPfrReceiptItemToProduct({ name: itemName, productId }),
    onSuccess: (res, productId) => onMapped(productId, res.product.sku),
  });

  const createProduct = useMutation({
    mutationFn: () => api.createPfrReceiptProduct({ name: itemName }),
    onSuccess: (res) => onMapped(res.product.id, res.product.sku),
  });

  const suggestionIds = new Set((suggestion?.suggestions ?? []).map((p) => p.id));
  const restProducts = (products ?? []).filter((p) => !suggestionIds.has(p.id));
  const hasSuggestions = (suggestion?.suggestions.length ?? 0) > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Link2 className="h-4 w-4" aria-hidden />
              Mapiraj artikal
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              "{cyrillicToLatin(itemName)}" → izaberi pravi artikal iz baze
            </p>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div className="border-b border-border px-5 py-2.5">
          <button
            type="button"
            disabled={createProduct.isPending}
            onClick={() => {
              if (
                window.confirm(
                  "Dodati ovaj artikal u bazu (naziv, cena i PDV sa računa) i odmah ga povezati?",
                )
              ) {
                createProduct.mutate();
              }
            }}
            className="w-full rounded-lg border border-dashed border-brand px-3 py-2 text-sm font-medium text-brand hover:bg-brand/5 disabled:opacity-60"
          >
            {createProduct.isPending ? "Dodajem artikal..." : "+ Dodaj kao novi artikal"}
          </button>
          {createProduct.isError && (
            <p className="mt-1 text-xs text-red-600">Artikal nije dodat. Pokušajte ponovo.</p>
          )}
        </div>

        {suggestion?.existingMapping && (
          <div className="border-b border-border bg-amber-50 px-5 py-2.5">
            <p className="text-xs text-amber-800">
              Već postoji mapiranje za ovaj naziv:{" "}
              <span className="font-medium">{suggestion.existingMapping.name}</span>{" "}
              <span className="text-amber-600">({suggestion.existingMapping.sku})</span> — ako je
              pogrešno, izaberi ispravan artikal ispod da ga izmeniš.
            </p>
          </div>
        )}

        <div className="border-b border-border px-5 py-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
            <input
              autoFocus
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Pretraži artikle po nazivu ili šifri..."
              className="h-10 w-full rounded-lg border border-border pl-9 pr-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {hasSuggestions && (
            <>
              <p className="bg-app-bg px-5 py-1.5 text-xs font-medium text-slate-500">
                Predlog — najsličniji artikli
              </p>
              <ul className="divide-y divide-border">
                {suggestion!.suggestions.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      disabled={map.isPending}
                      onClick={() => map.mutate(p.id)}
                      className="flex w-full items-center justify-between border-l-2 border-l-transparent bg-brand/5 px-5 py-2.5 text-left text-sm transition-colors hover:border-l-brand hover:bg-brand/10 disabled:opacity-50"
                    >
                      <span className="text-slate-800">{p.name}</span>
                      <span className="text-xs text-slate-400">{p.sku}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="border-t-2 border-dashed border-border" />
            </>
          )}

          {isLoading ? (
            <p className="px-5 py-4 text-sm text-slate-400">Učitavam...</p>
          ) : restProducts.length === 0 ? (
            !hasSuggestions && <p className="px-5 py-4 text-sm text-slate-400">Nema rezultata.</p>
          ) : (
            <ul className="divide-y divide-border">
              {restProducts.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    disabled={map.isPending}
                    onClick={() => map.mutate(p.id)}
                    className="flex w-full items-center justify-between border-l-2 border-l-transparent px-5 py-2.5 text-left text-sm transition-colors hover:border-l-brand hover:bg-brand/10 disabled:opacity-50"
                  >
                    <span className="text-slate-800">{p.name}</span>
                    <span className="text-xs text-slate-400">{p.sku}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {map.isError && (
          <p className="border-t border-border px-5 py-2 text-xs text-red-600">
            {map.error instanceof Error ? map.error.message : "Mapiranje nije uspelo."}
          </p>
        )}
      </div>
    </div>
  );
}

function UnmatchedTable({
  unmatched,
  onMapped,
}: {
  unmatched: PfrReceiptsConvertResultDto["unmatched"];
  onMapped: (itemName: string, productId: string, sku: string) => void;
}) {
  const [mappingItemName, setMappingItemName] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const { sortKey, sortDir, toggleSort, applySort } = useSort<
    PfrReceiptsConvertResultDto["unmatched"][number]
  >((item, key) => {
    if (key === "name") return cyrillicToLatin(item.name).toLowerCase();
    if (key === "gtin") return item.gtin ?? "";
    if (key === "quantity") return Number(item.quantity) || 0;
    if (key === "totalAmount") return Number(item.totalAmount) || 0;
    return "";
  });
  const needle = filter.trim().toLowerCase();
  const filtered = applySort(
    unmatched.filter(
      (item) =>
        cyrillicToLatin(item.name).toLowerCase().includes(needle) ||
        (item.gtin ?? "").toLowerCase().includes(needle),
    ),
  );
  const totalQuantity = filtered.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const totalValue = filtered.reduce((sum, item) => sum + (Number(item.totalAmount) || 0), 0);

  return (
    <>
      <div className="relative mb-2">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-amber-500" aria-hidden />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Pretraži po nazivu ili šifri (GTIN)..."
          className="h-8 w-64 rounded-lg border border-amber-200 bg-white pl-8 pr-2 text-xs focus:border-brand focus:outline-none"
        />
      </div>
      <div className="max-h-60 overflow-y-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-xs text-amber-700">
              <th className="pb-1 pr-3">Račun</th>
              <SortableTh label="Naziv artikla" sortKeyName="name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortableTh label="GTIN" sortKeyName="gtin" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortableTh label="Količina" sortKeyName="quantity" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortableTh label="Vrednost" sortKeyName="totalAmount" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <th className="pb-1"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((item) => (
              <tr key={item.itemId} className="border-t border-amber-200 transition-colors hover:bg-amber-100/70">
                <td className="py-1 pr-3 text-amber-900">{item.invoiceNumber}</td>
                <td className="py-1 pr-3 text-amber-900">{cyrillicToLatin(item.name)}</td>
                <td className="py-1 pr-3 text-amber-900">{item.gtin ?? "-"}</td>
                <td className="py-1 pr-3 text-amber-900">{item.quantity}</td>
                <td className="py-1 pr-3 text-amber-900">{item.totalAmount}</td>
                <td className="py-1">
                  <button
                    type="button"
                    onClick={() => setMappingItemName(item.name)}
                    className="text-xs font-medium text-brand hover:underline"
                  >
                    Mapiraj
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-amber-300 text-xs font-semibold text-amber-900">
              <td className="py-1.5 pr-3" colSpan={3}>
                Ukupno
              </td>
              <td className="py-1.5 pr-3">{totalQuantity}</td>
              <td className="py-1.5 pr-3">{totalValue.toFixed(2)}</td>
              <td className="py-1.5"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {mappingItemName && (
        <MapItemModal
          itemName={mappingItemName}
          onClose={() => setMappingItemName(null)}
          onMapped={(productId, sku) => {
            onMapped(mappingItemName, productId, sku);
            setMappingItemName(null);
          }}
        />
      )}
    </>
  );
}

function ImportDetailRow({ importId }: { importId: string }) {
  const queryClient = useQueryClient();
  const [mappingItemName, setMappingItemName] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["pfr-receipts-import-detail", importId],
    queryFn: () => api.getPfrReceiptsImportDetail(importId),
  });
  const { sortKey, sortDir, toggleSort, applySort } = useSort<NonNullable<typeof data>["items"][number]>(
    (it, key) => {
      if (key === "name") return cyrillicToLatin(it.name).toLowerCase();
      if (key === "originalName") return cyrillicToLatin(it.originalName).toLowerCase();
      if (key === "matchedSku") return it.matchedSku ?? "";
      if (key === "quantity") return Number(it.quantity) || 0;
      if (key === "totalAmount") return Number(it.totalAmount) || 0;
      if (key === "status") return it.status;
      return "";
    },
  );
  const needle = filter.trim().toLowerCase();
  const filteredItems = applySort(
    (data?.items ?? []).filter(
      (it) =>
        cyrillicToLatin(it.name).toLowerCase().includes(needle) ||
        cyrillicToLatin(it.originalName).toLowerCase().includes(needle) ||
        (it.matchedSku ?? "").toLowerCase().includes(needle) ||
        (it.gtin ?? "").toLowerCase().includes(needle),
    ),
  );
  const totalQuantity = filteredItems.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);
  const totalValue = filteredItems.reduce((sum, it) => sum + (Number(it.totalAmount) || 0), 0);

  const downloadAgain = useMutation({
    mutationFn: () => api.downloadPfrReceiptsImportXlsx(importId),
    onSuccess: (res) => downloadXlsxBase64(res.base64, res.fileName ?? "minimax_izvestaj.xlsx"),
  });

  const afterMapped = () => {
    // Mapiranje se primenjuje retroaktivno na SVE uvoze sa tim nazivom, ne
    // samo ovaj - zato osveži i listu (brojači) i sve otvorene detalje.
    queryClient.invalidateQueries({ queryKey: ["pfr-receipts-import-detail"] });
    queryClient.invalidateQueries({ queryKey: ["pfr-receipts-imports"] });
  };

  if (isLoading) {
    return <p className="px-4 py-3 text-xs text-slate-400">Učitavam...</p>;
  }
  if (!data) return null;

  return (
    <div className="border-t border-border bg-app-bg px-4 py-3">
      <div className="relative mb-2">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" aria-hidden />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Pretraži po nazivu ili šifri..."
          className="h-8 w-64 rounded-lg border border-border bg-white pl-8 pr-2 text-xs focus:border-brand focus:outline-none"
        />
      </div>
      <div className="max-h-80 overflow-y-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-xs text-slate-500">
              <th className="pb-1 pr-3">Račun</th>
              <SortableTh label="Naziv artikla" sortKeyName="name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortableTh label="Naziv iz poreske" sortKeyName="originalName" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortableTh label="Šifra (spojeno)" sortKeyName="matchedSku" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortableTh label="Količina" sortKeyName="quantity" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortableTh label="Vrednost" sortKeyName="totalAmount" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortableTh label="Status" sortKeyName="status" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <th className="pb-1"></th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((it) => (
              <tr key={it.id} className="border-t border-border transition-colors hover:bg-white">
                <td className="py-1 pr-3 text-slate-600">{it.invoiceNumber ?? "-"}</td>
                <td className="py-1 pr-3 text-slate-800">{cyrillicToLatin(it.name)}</td>
                <td className="py-1 pr-3 text-slate-500">
                  {it.name !== it.originalName ? cyrillicToLatin(it.originalName) : "-"}
                </td>
                <td className="py-1 pr-3 text-slate-800">{it.matchedSku ?? "-"}</td>
                <td className="py-1 pr-3 text-slate-600">{it.quantity}</td>
                <td className="py-1 pr-3 text-slate-600">{it.totalAmount}</td>
                <td className="py-1">
                  {it.status === "MATCHED" ? (
                    <span className="text-xs font-medium text-emerald-600">Uspešno</span>
                  ) : (
                    <span className="text-xs font-medium text-red-600">Neuspešno</span>
                  )}
                </td>
                <td className="py-1">
                  <button
                    type="button"
                    onClick={() => setMappingItemName(it.originalName)}
                    className="text-xs font-medium text-brand hover:underline"
                  >
                    {it.status === "UNMATCHED" ? "Mapiraj" : "Izmeni mapiranje"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-300 text-xs font-semibold text-slate-700">
              <td className="py-1.5 pr-3" colSpan={4}>
                Ukupno
              </td>
              <td className="py-1.5 pr-3">{totalQuantity}</td>
              <td className="py-1.5 pr-3">{totalValue.toFixed(2)}</td>
              <td className="py-1.5" colSpan={2}></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="mt-3 flex justify-end">
        <Button size="sm" disabled={downloadAgain.isPending} onClick={() => downloadAgain.mutate()}>
          <Download className="h-3.5 w-3.5" aria-hidden />
          {downloadAgain.isPending ? "Pripremam..." : "Preuzmi Excel fajl (.xlsx)"}
        </Button>
      </div>

      {mappingItemName && (
        <MapItemModal
          itemName={mappingItemName}
          onClose={() => setMappingItemName(null)}
          onMapped={() => {
            setMappingItemName(null);
            afterMapped();
          }}
        />
      )}
    </div>
  );
}

/**
 * Jedan red u "Istorija uvoza" - izdvojeno u poseban komponent jer treba
 * sopstveni useMutation za direktno preuzimanje Excel fajla (dugme mora da
 * postoji odmah pored brisanja, i pre i posle otvaranja reda) - vidi
 * razgovor "stavis pored brisanja ikonu za excel... da ostane kada se
 * otvori". Hook-ovi se ne mogu zvati unutar .map() u roditelju.
 */
function ImportHistoryRow({
  imp,
  expanded,
  onToggle,
  onDelete,
  deletePending,
}: {
  imp: PfrReceiptsImportSummaryDto;
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  deletePending: boolean;
}) {
  const downloadXlsx = useMutation({
    mutationFn: () => api.downloadPfrReceiptsImportXlsx(imp.id),
    onSuccess: (res) =>
      downloadXlsxBase64(res.base64, res.fileName ?? "minimax_izvestaj.xlsx"),
  });

  return (
    <div>
      <div className="flex w-full items-center justify-between border-t border-border px-4 py-3 hover:bg-app-bg">
        <button type="button" onClick={onToggle} className="flex flex-1 items-center gap-2 text-left">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-slate-400" aria-hidden />
          ) : (
            <ChevronRight className="h-4 w-4 text-slate-400" aria-hidden />
          )}
          <div>
            <p className="text-sm font-medium text-slate-800">
              {new Date(imp.createdAt).toLocaleString("sr-RS")}
            </p>
            <p className="text-xs text-slate-400">
              {imp.receiptCount} računa, {imp.itemCount} stavki
            </p>
          </div>
        </button>
        <div className="flex items-center gap-3 text-xs">
          <span className="font-medium text-emerald-600">{imp.matchedCount} uspešno</span>
          {imp.unmatchedCount > 0 && (
            <span className="font-medium text-red-600">{imp.unmatchedCount} neuspešno</span>
          )}
          <button
            type="button"
            title="Preuzmi Excel fajl (.xlsx)"
            disabled={downloadXlsx.isPending}
            onClick={() => downloadXlsx.mutate()}
            className="rounded-md p-1.5 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-50"
          >
            <FileSpreadsheet className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            title="Obriši unos"
            disabled={deletePending}
            onClick={onDelete}
            className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
      {expanded && <ImportDetailRow importId={imp.id} />}
    </div>
  );
}

export function PfrReceiptsExportTab() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [expandedImportId, setExpandedImportId] = useState<string | null>(null);
  // Lokalna, izmenljiva kopija rezultata konverzije - kad se artikal
  // mapira, odmah ga uklanjamo iz "nespojeno" liste bez čekanja na ponovni
  // upload (React Query mutation "data" se ne može invalidate-ovati kao keš).
  const [localResult, setLocalResult] = useState<PfrReceiptsConvertResultDto | null>(null);

  const convert = useMutation({
    mutationFn: (file: File) => api.convertPfrReceiptsJsonToMinimaxXlsx(file),
    onSuccess: (data) => {
      setLocalResult(data);
      queryClient.invalidateQueries({ queryKey: ["pfr-receipts-imports"] });
    },
  });

  const importsQuery = useQuery({
    queryKey: ["pfr-receipts-imports"],
    queryFn: () => api.listPfrReceiptsImports(),
    retry: false,
  });

  const deleteImport = useMutation({
    mutationFn: (importId: string) => api.deletePfrReceiptsImport(importId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pfr-receipts-imports"] });
      setExpandedImportId(null);
    },
  });

  const handleFileSelect = (file: File) => {
    setFileName(file.name);
    convert.mutate(file);
  };

  const downloadXlsx = () => {
    if (!localResult) return;
    downloadXlsxBase64(localResult.base64, localResult.fileName);
  };

  const handleMappedInLocalResult = (itemName: string) => {
    setLocalResult((prev) => {
      if (!prev) return prev;
      const remaining = prev.unmatched.filter((u) => u.name !== itemName);
      const removed = prev.unmatched.length - remaining.length;
      return {
        ...prev,
        unmatched: remaining,
        unmatchedCount: prev.unmatchedCount - removed,
        matchedCount: prev.matchedCount + removed,
      };
    });
    queryClient.invalidateQueries({ queryKey: ["pfr-receipts-import-detail"] });
    queryClient.invalidateQueries({ queryKey: ["pfr-receipts-imports"] });
  };

  const sortedImports = useMemo(() => importsQuery.data ?? [], [importsQuery.data]);

  // Isti gate kao tab "MiniMax" u Podešavanjima - vidi razgovor "oba taba
  // moraju biti gejtovana iza MiniMax modula" i
  // pfr-receipts-export.controller.ts (@RequireModule("minimax_api")).
  if (
    importsQuery.isError &&
    importsQuery.error instanceof ApiError &&
    importsQuery.error.response.errorCode === "MODULE_NOT_AVAILABLE"
  ) {
    return <ModuleGateNotice error={importsQuery.error} moduleKey="minimax_api" />;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <h2 className="mb-1 text-sm font-semibold text-slate-800">
          Uvoz JSON fajla (Poreska uprava) → MiniMax XLSX
        </h2>
        <p className="mb-3 text-xs text-slate-400">
          Otpremi JSON izvoz "Rezultat pretrage računa" sa portala Poreske uprave - HAFiskal ga spaja sa
          artiklima koje već imaš u bazi (uneti preko XML uvoza, po šifri/barkodu ili nazivu, jer Poreska
          uprava/MiniMax često ne šalje pouzdanu šifru artikla u JSON-u) i konvertuje u XLSX po MiniMax
          "Uvoz reda za izdavanje stranci" formatu.
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
          }}
        />
        <Button
          variant="secondary"
          disabled={convert.isPending}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="h-4 w-4" aria-hidden />
          {convert.isPending ? "Obrađujem..." : "Upload JSON fajl Poreske"}
        </Button>
        {fileName && <span className="ml-2 text-xs text-slate-400">{fileName}</span>}

        {convert.isError && (
          <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {convert.error instanceof Error ? convert.error.message : "Konverzija nije uspela."}
          </p>
        )}

        {localResult && (
          <div className="mt-3 rounded-lg bg-emerald-50 px-3 py-2">
            <p className="flex items-center gap-1.5 text-sm text-emerald-700">
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              Obrađeno — {localResult.receiptCount} računa, {localResult.itemCount} stavki (
              {localResult.matchedCount} spojeno sa bazom, {localResult.unmatchedCount} nije pronađeno).
            </p>
            <div className="mt-2">
              <Button size="sm" onClick={downloadXlsx}>
                <Download className="h-3.5 w-3.5" aria-hidden />
                Skini XLSX
              </Button>
            </div>
          </div>
        )}

        {localResult && localResult.unmatched.length > 0 && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="mb-2 flex items-center gap-1.5 text-sm font-medium text-amber-800">
              <AlertTriangle className="h-4 w-4" aria-hidden />
              Artikli koji NISU pronađeni u HAFiskal bazi ({localResult.unmatched.length})
            </p>
            <p className="mb-2 text-xs text-amber-700">
              Klikni "Mapiraj" da ručno povežeš naziv sa pravim artiklom - zapamtiće se za ubuduće, ili
              dodaj/ispravi artikal u bazi pa ponovi uvoz.
            </p>
            <UnmatchedTable
              unmatched={localResult.unmatched}
              onMapped={(itemName) => handleMappedInLocalResult(itemName)}
            />
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        <div className="border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-800">Istorija uvoza</h2>
        </div>
        {sortedImports.length === 0 ? (
          <p className="p-4 text-sm text-slate-400">Još uvek nema uvoza.</p>
        ) : (
          <div>
            {sortedImports.map((imp) => (
              <ImportHistoryRow
                key={imp.id}
                imp={imp}
                expanded={expandedImportId === imp.id}
                onToggle={() => setExpandedImportId(expandedImportId === imp.id ? null : imp.id)}
                deletePending={deleteImport.isPending}
                onDelete={() => {
                  if (window.confirm("Obrisati ovaj uvoz i sve njegove stavke?")) {
                    deleteImport.mutate(imp.id);
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
